import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatIDR } from "@/data/products";

export default function CartDrawer() {
  const { t, cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartTotal, setCheckoutOpen } =
    useStore();

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-ink-soft"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <h3 className="font-display text-lg font-bold tracking-wide text-white">
                {t.cart.title}
              </h3>
              <button
                data-testid="cart-close-btn"
                onClick={() => setCartOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-mist transition-colors hover:border-ember hover:text-ember"
                aria-label="Close cart"
              >
                <X size={15} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {cart.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
                  <p data-testid="cart-empty-text" className="text-sm text-mist">
                    {t.cart.empty}
                  </p>
                  <button
                    data-testid="cart-browse-btn"
                    onClick={() => {
                      setCartOpen(false);
                      const el = document.getElementById("koleksi");
                      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
                    }}
                    className="rounded-full border border-ember px-6 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest text-ember transition-colors hover:bg-ember hover:text-black"
                  >
                    {t.cart.emptyCta}
                  </button>
                </div>
              ) : (
                <ul className="flex flex-col gap-5">
                  {cart.map((item) => (
                    <li
                      key={item.id}
                      data-testid={`cart-item-${item.id}`}
                      className="flex gap-4 rounded-2xl border border-white/10 bg-ink-card p-3"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-20 w-16 shrink-0 rounded-xl object-cover"
                      />
                      <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate font-display text-sm font-bold text-white">
                              {item.name}
                            </p>
                            <p className="mt-0.5 font-mono text-xs text-ember-light">
                              {formatIDR(item.price)}
                            </p>
                          </div>
                          <button
                            data-testid={`cart-remove-${item.id}`}
                            onClick={() => removeFromCart(item.id)}
                            className="text-zinc-500 transition-colors hover:text-red-400"
                            aria-label="Remove item"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            data-testid={`cart-qty-minus-${item.id}`}
                            onClick={() => updateQty(item.id, -1)}
                            className="flex h-7 w-7 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-ember hover:text-ember"
                            aria-label="Decrease quantity"
                          >
                            <Minus size={12} />
                          </button>
                          <span
                            data-testid={`cart-qty-${item.id}`}
                            className="w-5 text-center font-mono text-sm text-white"
                          >
                            {item.qty}
                          </span>
                          <button
                            data-testid={`cart-qty-plus-${item.id}`}
                            onClick={() => updateQty(item.id, 1)}
                            className="flex h-7 w-7 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-ember hover:text-ember"
                            aria-label="Increase quantity"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-white/10 px-6 py-5">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-mist">
                    {t.cart.subtotal}
                  </span>
                  <span data-testid="cart-subtotal" className="font-display text-xl font-bold text-ember-light">
                    {formatIDR(cartTotal)}
                  </span>
                </div>
                <p className="mb-5 text-xs text-zinc-500">{t.cart.note}</p>
                <button
                  data-testid="cart-checkout-btn"
                  onClick={() => {
                    setCartOpen(false);
                    setCheckoutOpen(true);
                  }}
                  className="w-full rounded-full bg-ember py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-all duration-300 hover:bg-ember-light hover:shadow-[0_0_32px_rgba(255,107,0,0.4)]"
                >
                  {t.cart.checkout}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
